import { useEffect, useRef, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material'
import { authService } from '../services/auth-service'
import { SiteNav } from '../components/site/SiteNav'
import { SiteFooter } from '../components/site/SiteFooter'

/**
 * Email verification landing page.
 *
 * The verification email has always linked here, but this route did not exist:
 * every new user who clicked the link in their inbox got a bare "Not Found",
 * with no way forward and no idea their account was fine. Signup was broken
 * end to end.
 *
 * The token arrives in the query string, so it is read from `window.location`
 * rather than a typed search schema — a malformed link must render an
 * explanation, not throw a validation error at someone who just wants in.
 */
export const Route = createFileRoute('/verify')({
  component: VerifyEmail,
})

type State = 'working' | 'done' | 'failed' | 'missing'

function VerifyEmail() {
  const navigate = useNavigate()
  const [state, setState] = useState<State>('working')
  const [message, setMessage] = useState('')
  // React 18 StrictMode mounts effects twice in development; without this the
  // token would be spent on the first call and the second would report it
  // invalid, which looks exactly like a real failure.
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) {
      setState('missing')
      return
    }

    authService
      .verifyEmail(token)
      .then(() => {
        setState('done')
        // Send them where they were trying to go, rather than making them
        // hunt for the sign-in link after a successful verification.
        setTimeout(() => navigate({ to: '/login' }), 2500)
      })
      .catch((e: unknown) => {
        setState('failed')
        setMessage(
          e instanceof Error && e.message
            ? e.message
            : 'That link could not be verified.',
        )
      })
  }, [navigate])

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <SiteNav />
      <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 8, md: 14 } }}>
        {state === 'working' && (
          <Box sx={{ display: 'grid', placeItems: 'center', gap: 2 }}>
            <CircularProgress size={28} />
            <Typography sx={{ color: 'var(--text-secondary)' }}>
              Verifying your email…
            </Typography>
          </Box>
        )}

        {state === 'done' && (
          <Box>
            <Typography component="h1" sx={{
              fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5,
            }}>
              You&rsquo;re verified.
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              Your email is confirmed and your account is ready. Taking you to
              sign in.
            </Typography>
            <Button component={Link} to="/login" variant="contained">Sign in</Button>
          </Box>
        )}

        {state === 'failed' && (
          <Box>
            <Typography component="h1" sx={{
              fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5,
            }}>
              That link didn&rsquo;t work.
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 1.5 }}>{message}</Typography>
            {/* The overwhelmingly common causes, so nobody has to guess. */}
            <Typography sx={{ color: 'var(--text-faint)', mb: 4, lineHeight: 1.7 }}>
              Verification links expire after 24 hours, and each one can only be
              used once — if you have already verified, just sign in.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component={Link} to="/login" variant="contained">Try signing in</Button>
              <Button component={Link} to="/register" variant="outlined">Create an account</Button>
            </Box>
          </Box>
        )}

        {state === 'missing' && (
          <Box>
            <Typography component="h1" sx={{
              fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5,
            }}>
              No verification token.
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              This page needs the link from your verification email. Some mail
              clients trim long URLs — try copying the whole link across.
            </Typography>
            <Button component={Link} to="/login" variant="contained">Go to sign in</Button>
          </Box>
        )}
      </Container>
      <SiteFooter />
    </Box>
  )
}
